import { call } from '@advanza/api'
import { Button } from '@advanza/ui'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import InputErrorMessage from '@trustoo/components/input/InputErrorMessage'
import InputField from '@trustoo/components/input/InputField'
import TextAreaField from '@trustoo/components/input/TextAreaField'
import BaseModal, {
    BaseModalFooter,
    BaseModalHeader,
    BaseModalMainContent,
} from '@trustoo/components/modal/BaseModal'
import {
    affectedRowCount,
    buildBulkRequest,
    bulkConfirmLabel,
    bulkFailureMessage,
    bulkLimitMessage,
    bulkModalTitle,
    bulkSuccessMessage,
    confirmSentence,
    deleteCountConfirmed,
    formatKeywordMappingCount,
    isFilterTarget,
    isOverBulkLimit,
    KEYWORD_MAPPING_BULK_PREVIEW_URL,
    KeywordMappingBulkAction,
    KeywordMappingBulkPreview,
    KeywordMappingBulkTarget,
    outsideSelectionMessage,
    readBulkPreview,
    readBulkResultCount,
} from 'components/keywordMappings/keywordMappingBulk'
import {
    h1HelpText,
    INVALIDATE_KEYWORD_MAPPING_SEARCH_INTENTS,
    INVALIDATE_KEYWORD_MAPPINGS,
    KEYWORD_MAPPING_FOCUS_RING,
    readFieldErrors,
    readGenericError,
    withoutField,
} from 'components/keywordMappings/keywordMappingModel'
import { useToast } from 'components/misc/Toaster'
import { useAppDispatch } from 'hooks/hooks'
import { FormEvent, useEffect, useRef, useState } from 'react'

interface KeywordMappingBulkModalProps {
    action: KeywordMappingBulkAction
    target: KeywordMappingBulkTarget
    h1Placeholders: string[]
    initialH1Text?: string
    groupKeywordCount?: number
}

const PREVIEW_FAILED = 'Could not check how many keywords this changes.'

const FIELD_CLASS = `${KEYWORD_MAPPING_FOCUS_RING} focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-[#5a8bfc]`

function h1FieldError(errors: Record<string, string>): string | undefined {
    if (errors.h1_text !== undefined) return errors.h1_text
    return errors.h1Text
}

const KeywordMappingBulkModal = NiceModal.create(
    ({
        action,
        target,
        h1Placeholders,
        initialH1Text,
        groupKeywordCount,
    }: KeywordMappingBulkModalProps) => {
        const modal = useModal()
        const dispatch = useAppDispatch()
        const { addToast } = useToast()
        const activeRef = useRef(true)
        const savingRef = useRef(false)
        const [preview, setPreview] = useState<KeywordMappingBulkPreview | null>(null)
        const [previewError, setPreviewError] = useState('')
        const [isLoadingPreview, setIsLoadingPreview] = useState(true)
        const [h1Text, setH1Text] = useState(initialH1Text ?? '')
        const [typedCount, setTypedCount] = useState('')
        const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
        const [formError, setFormError] = useState('')
        const [isSaving, setIsSaving] = useState(false)

        const title = bulkModalTitle(action)
        const needsCountConfirm = action === 'delete' && isFilterTarget(target)
        const overLimit = preview !== null && isOverBulkLimit(action, preview)
        const affected = preview === null ? null : affectedRowCount(action, preview)
        const countConfirmed =
            preview !== null && deleteCountConfirmed(typedCount, preview.rowCount)
        const confirmDisabled =
            isSaving ||
            isLoadingPreview ||
            preview === null ||
            previewError !== '' ||
            overLimit ||
            affected === 0 ||
            (action === 'h1' && h1Text.trim() === '') ||
            (needsCountConfirm && countConfirmed === false)

        useEffect(() => {
            activeRef.current = true
            let cancelled = false

            void call(KEYWORD_MAPPING_BULK_PREVIEW_URL, { json: { target } })
                .then((response: unknown) => {
                    if (cancelled || activeRef.current === false) return
                    const next = readBulkPreview(response)
                    if (next === null) {
                        setPreviewError(PREVIEW_FAILED)
                        return
                    }
                    setPreview(next)
                })
                .catch((response: unknown) => {
                    if (cancelled || activeRef.current === false) return
                    setPreviewError(readGenericError(response, PREVIEW_FAILED))
                })
                .finally(() => {
                    if (cancelled || activeRef.current === false) return
                    setIsLoadingPreview(false)
                })

            return () => {
                cancelled = true
                activeRef.current = false
            }
        }, [target])

        const applyFailure = (response: unknown) => {
            const errors = readFieldErrors(response)
            setFieldErrors(errors)
            const others = Object.entries(errors)
                .filter(([field]) => field !== 'h1_text' && field !== 'h1Text')
                .map(([, message]) => message)
            if (others.length > 0) {
                setFormError(others.join(' '))
                return
            }
            if (h1FieldError(errors) !== undefined) return
            setFormError(readGenericError(response, bulkFailureMessage(action)))
        }

        const runSave = async () => {
            if (savingRef.current) return
            if (preview === null) return
            if (confirmDisabled) return

            savingRef.current = true
            setIsSaving(true)
            setFormError('')
            const request = buildBulkRequest(action, target, h1Text, preview.rowCount)

            try {
                const response: unknown = await call(request.url, { json: request.body })
                // Back closes the modal even while saving; the write landed, so the lists still refresh.
                const countField = action === 'delete' ? 'deletedRows' : 'updatedRows'
                dispatch({ type: INVALIDATE_KEYWORD_MAPPINGS })
                dispatch({ type: INVALIDATE_KEYWORD_MAPPING_SEARCH_INTENTS })
                addToast({
                    msg: bulkSuccessMessage(action, readBulkResultCount(response, countField)),
                })
                if (activeRef.current === false) return
                modal.resolve(true)
                modal.hide()
            } catch (response: unknown) {
                if (activeRef.current === false) return
                applyFailure(response)
            } finally {
                savingRef.current = false
                if (activeRef.current) setIsSaving(false)
            }
        }

        const onSubmit = (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            void runSave()
        }

        const onCancel = () => {
            if (isSaving) return
            modal.resolve(false)
            modal.hide()
        }

        const outsideMessage =
            action === 'h1' && preview !== null
                ? outsideSelectionMessage(preview.outsideTargetCount)
                : null

        return (
            <BaseModal preventClose={isSaving} closeButtonLabel="Close" aria-label={title}>
                <form className="flex min-h-0 w-full flex-1 flex-col" onSubmit={onSubmit}>
                    <BaseModalHeader>
                        <h3 className="m-0">{title}</h3>
                    </BaseModalHeader>
                    <BaseModalMainContent className="flex flex-col gap-4">
                        {isLoadingPreview && (
                            <p className="m-0 text-sm" aria-live="polite">
                                Checking how many keywords this changes…
                            </p>
                        )}
                        {previewError !== '' && (
                            <div aria-live="polite">
                                <InputErrorMessage error={previewError} />
                            </div>
                        )}
                        {preview !== null && (
                            <div className="flex flex-col gap-2 text-sm text-[#344054]">
                                <p className="m-0">
                                    {confirmSentence(action, preview, groupKeywordCount)}
                                </p>
                                {outsideMessage !== null && <p className="m-0">{outsideMessage}</p>}
                                {overLimit && (
                                    <p className="m-0 font-semibold text-[#9a3412]">
                                        {bulkLimitMessage(preview.maxRows)}
                                    </p>
                                )}
                            </div>
                        )}
                        {action === 'h1' && (
                            <div>
                                <TextAreaField
                                    id="keyword-mapping-bulk-h1"
                                    name="h1Text"
                                    legend="H1 text"
                                    aria-label="H1 text"
                                    aria-describedby="keyword-mapping-bulk-h1-help"
                                    rows={4}
                                    value={h1Text}
                                    disabled={isSaving}
                                    error={h1FieldError(fieldErrors)}
                                    textAreaClassName={`min-h-[105px] ${KEYWORD_MAPPING_FOCUS_RING}`}
                                    labelClassName={FIELD_CLASS}
                                    onChange={(event) => {
                                        setH1Text(event.target.value)
                                        setFieldErrors((current) =>
                                            withoutField(
                                                withoutField(current, 'h1_text'),
                                                'h1Text',
                                            ),
                                        )
                                    }}
                                />
                                <p
                                    id="keyword-mapping-bulk-h1-help"
                                    className="mt-1 text-sm text-[#666666]">
                                    {h1HelpText(h1Placeholders)}
                                </p>
                            </div>
                        )}
                        {needsCountConfirm && preview !== null && (
                            <InputField
                                id="keyword-mapping-bulk-confirm-count"
                                name="confirmCount"
                                legend={`Type ${formatKeywordMappingCount(preview.rowCount)} to confirm`}
                                aria-label={`Type ${formatKeywordMappingCount(preview.rowCount)} to confirm deletion`}
                                inputMode="numeric"
                                autoComplete="off"
                                value={typedCount}
                                disabled={isSaving}
                                inputClassName={KEYWORD_MAPPING_FOCUS_RING}
                                labelClassName={FIELD_CLASS}
                                onChange={(event) => setTypedCount(event.target.value)}
                            />
                        )}
                        {formError !== '' && (
                            <div aria-live="polite">
                                <InputErrorMessage error={formError} />
                            </div>
                        )}
                    </BaseModalMainContent>
                    <BaseModalFooter className="flex items-center justify-end gap-2">
                        <Button
                            type="button"
                            name="text"
                            className={KEYWORD_MAPPING_FOCUS_RING}
                            onClick={onCancel}
                            disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            appearance="primary"
                            className={KEYWORD_MAPPING_FOCUS_RING}
                            disabled={confirmDisabled}>
                            {bulkConfirmLabel(action, isSaving)}
                        </Button>
                    </BaseModalFooter>
                </form>
            </BaseModal>
        )
    },
)

export default KeywordMappingBulkModal
